/**
 * Remark plugin: promote a title-like paragraph immediately before a GFM table
 * onto the table node as `hProperties.dataTableTitle`, and remove the paragraph
 * so StreamingText can render it as Table's `title` prop.
 */

interface MdastNode {
  type: string;
  value?: string;
  children?: MdastNode[];
  data?: {
    hProperties?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

const MAX_TITLE_LENGTH = 120;

function mdastText(node: MdastNode): string {
  if (typeof node.value === 'string') return node.value;
  if (!node.children) return '';
  return node.children.map(mdastText).join('');
}

const MAX_COLON_TITLE_LENGTH = 60;

/**
 * Paragraph is wholly bold/italic/strikethrough (a trailing colon outside the emphasis is fine) —
 * a genuine table label like `**Top sources:**`, not bare prose that merely lacks a full stop.
 */
function isLabelShaped(node: MdastNode): boolean {
  if (!node.children || node.children.length === 0) return false;
  let hasEmphasis = false;
  for (const child of node.children) {
    if (child.type === 'strong' || child.type === 'emphasis' || child.type === 'delete') {
      hasEmphasis = true;
      continue;
    }
    // Only whitespace / a stray colon may sit outside the emphasis span.
    if (child.type === 'text' && /^[\s:]*$/.test(child.value ?? '')) continue;
    return false;
  }
  return hasEmphasis;
}

/**
 * Decide whether a paragraph sitting directly above a table should become the
 * table title. Keeps real prose paragraphs as body text — promoting them would
 * delete them from the message (the plugin removes the promoted paragraph).
 */
export function titleFromPrecedingParagraph(node: MdastNode): string | null {
  if (node.type !== 'paragraph') return null;

  const text = mdastText(node).trim();
  if (!text || text.length > MAX_TITLE_LENGTH) return null;
  if (text.includes('\n')) return null;

  const endsWithColon = /:\s*$/.test(text);
  const endsWithSentence = /[.!?]\s*$/.test(text);

  // A wholly bold/italic caption is a strong signal of intent — accept it (with or without a colon).
  if (isLabelShaped(node) && !endsWithSentence) return text;
  // A short plain colon-terminated label ("Top sources:") — but not a long colon-ended prose caveat.
  if (endsWithColon && text.length <= MAX_COLON_TITLE_LENGTH) return text;

  return null;
}

function promoteInParent(parent: MdastNode): void {
  const children = parent.children;
  if (!children) return;

  for (const child of children) {
    if (child.children) promoteInParent(child);
  }

  for (let i = 1; i < children.length; i++) {
    const node = children[i];
    if (node?.type !== 'table') continue;

    const prev = children[i - 1];
    if (!prev) continue;

    const title = titleFromPrecedingParagraph(prev);
    if (!title) continue;

    node.data = node.data ?? {};
    node.data.hProperties = {
      ...(node.data.hProperties ?? {}),
      dataTableTitle: title,
    };
    children.splice(i - 1, 1);
    // Table shifted to i-1; next for-loop step looks at the node after the table.
  }
}

/** Remark plugin factory for react-markdown's `remarkPlugins`. */
export function remarkPromoteTableTitles() {
  return (tree: MdastNode) => {
    promoteInParent(tree);
  };
}

/** Read the title stashed on a hast `<table>` by {@link remarkPromoteTableTitles}. */
export function readMarkdownTableTitle(node: unknown): string | undefined {
  if (typeof node !== 'object' || node === null) return undefined;
  const properties = (node as { properties?: Record<string, unknown> }).properties;
  if (!properties) return undefined;
  const raw = properties['dataTableTitle'] ?? properties['data-table-title'];
  return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : undefined;
}
